import { z } from 'zod'

/**
 * What one probe run reports back (PROMPT.md §5.2). The shape mirrors the JSON line the probe
 * script prints; every section is nullable because a server may lack the source it comes from
 * (no /proc/net/dev in a container, no apt on Alpine, no readable lastb).
 */

export const osInfoSchema = z.object({
  /** Pretty name from /etc/os-release, e.g. "Debian GNU/Linux 12 (bookworm)". */
  name: z.string(),
  /** The `ID` field: debian, ubuntu, alpine … */
  id: z.string(),
  version: z.string(),
})

export const hostFactsSchema = z.object({
  hostname: z.string(),
  os: osInfoSchema.nullable(),
  kernel: z.string(),
  arch: z.string(),
  uptimeSec: z.number().nonnegative(),
})

export const loadFactsSchema = z.object({
  one: z.number().nonnegative(),
  five: z.number().nonnegative(),
  fifteen: z.number().nonnegative(),
})

export const cpuFactsSchema = z.object({
  cores: z.number().int().positive(),
  /** Measured from two /proc/stat samples, not parsed out of top. */
  usagePct: z.number().min(0).max(100).nullable(),
})

export const memoryFactsSchema = z.object({
  totalBytes: z.number().nonnegative(),
  /** total - available, which is what "used" means to a person. */
  usedBytes: z.number().nonnegative(),
  availableBytes: z.number().nonnegative(),
  swapTotalBytes: z.number().nonnegative(),
  swapUsedBytes: z.number().nonnegative(),
})

export const diskFactsSchema = z.object({
  mount: z.string(),
  device: z.string(),
  totalBytes: z.number().nonnegative(),
  usedBytes: z.number().nonnegative(),
  availableBytes: z.number().nonnegative(),
  usedPct: z.number().min(0).max(100),
  /** Null on filesystems that do not report inodes, like btrfs. */
  inodesUsedPct: z.number().min(0).max(100).nullable(),
})

export const networkFactsSchema = z.object({
  name: z.string(),
  rxBytes: z.number().nonnegative(),
  txBytes: z.number().nonnegative(),
})

export const listenerFactsSchema = z.object({
  port: z.number().int().positive(),
  address: z.string(),
  /** Only readable as root; null otherwise. */
  process: z.string().nullable(),
})

/**
 * What the server can do. The UI shows no button for anything false here — §5.2 calls this the
 * central UI principle, not a nicety.
 */
export const capabilitiesSchema = z.object({
  pm2: z.boolean(),
  docker: z.boolean(),
  systemctl: z.boolean(),
  ufw: z.boolean(),
  fail2ban: z.boolean(),
  certbot: z.boolean(),
  psql: z.boolean(),
  mysql: z.boolean(),
  nginx: z.boolean(),
  /** `sudo -n true` went through, so privileged commands need no password. */
  sudoNoPassword: z.boolean(),
})
export type Capabilities = z.infer<typeof capabilitiesSchema>

export const updateFactsSchema = z.object({
  /** Null when the server has no apt. */
  pending: z.number().int().nonnegative().nullable(),
  security: z.number().int().nonnegative().nullable(),
})

export const failedLoginSchema = z.object({
  user: z.string(),
  from: z.string(),
  at: z.string(),
})

export const serverFactsSchema = z.object({
  collectedAt: z.iso.datetime(),
  /** How long the probe itself took on the server, for the "one round trip" claim (§5.2). */
  durationMs: z.number().nonnegative(),
  host: hostFactsSchema,
  load: loadFactsSchema.nullable(),
  cpu: cpuFactsSchema,
  memory: memoryFactsSchema.nullable(),
  disks: z.array(diskFactsSchema),
  network: z.array(networkFactsSchema),
  listeners: z.array(listenerFactsSchema),
  capabilities: capabilitiesSchema,
  updates: updateFactsSchema,
  /** Null when lastb is missing or unreadable, which is the normal case for a non-root user. */
  failedLogins: z.array(failedLoginSchema).nullable(),
})
export type ServerFacts = z.infer<typeof serverFactsSchema>

export const factsResponseSchema = z.object({
  facts: serverFactsSchema,
  /** True when the capability part came from the cache rather than from this run (§5.2). */
  capabilitiesCached: z.boolean(),
})
export type FactsResponse = z.infer<typeof factsResponseSchema>

export const factsQuerySchema = z.object({
  /** Forces a fresh capability detection instead of using the ten minute cache. */
  refresh: z.stringbool().default(false),
})

/**
 * What the probe script itself prints. Capabilities and updates are nullable because the caller
 * can switch that (slow) detection off and fill it from its cache instead; the timestamps are
 * added by the backend, whose clock is the one every comparison uses.
 */
export const probeOutputSchema = serverFactsSchema
  .omit({ collectedAt: true, durationMs: true, capabilities: true, updates: true })
  .extend({
    capabilities: capabilitiesSchema.nullable(),
    updates: updateFactsSchema.nullable(),
  })
export type ProbeOutput = z.infer<typeof probeOutputSchema>
